// Loyalty ledger tab for Customer 360.

import type { ColumnDef } from '@bbpos/ui';
import { Badge } from '@bbpos/ui';
import { ReportTable } from '@/features/reports';
import type { LoyaltyLedgerRow } from '../types';
import { formatDateTime } from '../format';

type Row = LoyaltyLedgerRow;

const TYPE_VARIANT: Record<Row['type'], 'success' | 'info' | 'warning' | 'danger'> = {
  EARN: 'success',
  REDEEM: 'info',
  ADJUST: 'warning',
  EXPIRE: 'danger',
};

const columns: ColumnDef<Row>[] = [
  {
    key: 'date',
    header: 'Date',
    render: (_v, row) => <span className="text-sm text-neutral-600 dark:text-neutral-300 tabular-nums">{formatDateTime(row.date)}</span>,
  },
  {
    key: 'type',
    header: 'Type',
    render: (_v, row) => <Badge variant={TYPE_VARIANT[row.type]} size="sm">{row.type}</Badge>,
  },
  {
    key: 'description',
    header: 'Description',
    render: (_v, row) => (
      <div>
        <p className="text-sm text-neutral-800 dark:text-neutral-200">{row.description ?? '—'}</p>
        {row.bill_number ? <p className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{row.bill_number}</p> : null}
      </div>
    ),
  },
  {
    key: 'points',
    header: 'Points',
    numeric: true,
    render: (_v, row) => {
      const sign = row.points >= 0 ? '+' : '';
      const cls = row.points >= 0 ? 'text-success-600 dark:text-success-300' : 'text-danger-600 dark:text-danger-300';
      return <span className={`text-sm font-bold tabular-nums ${cls}`}>{sign}{row.points.toLocaleString('en-IN')}</span>;
    },
  },
  {
    key: 'balance_after',
    header: 'Balance',
    numeric: true,
    render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">{row.balance_after.toLocaleString('en-IN')}</span>,
  },
];

export interface LoyaltyLedgerTableProps {
  rows: Row[];
  loading?: boolean;
}

export function LoyaltyLedgerTable({ rows, loading }: LoyaltyLedgerTableProps) {
  return (
    <ReportTable<Row>
      title="Loyalty Ledger"
      subtitle="Points earned, redeemed, adjusted and expired."
      columns={columns}
      data={rows}
      loading={loading}
      totalCount={rows.length}
      emptyTitle="No loyalty activity"
      emptyDescription="Enroll the customer and they'll start earning points on every purchase."
    />
  );
}
